export interface ApiFieldError {
  field: string;
  code: string;
  message: string;
}

export interface ApiErrorPayload {
  code: string;
  message: string;
  detail?: unknown;
  request_id?: string;
  field_errors?: ApiFieldError[];
}

interface ErrorResponse {
  status?: unknown;
  data?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function statusCode(status: unknown): number {
  return typeof status === 'number' && Number.isFinite(status) ? status : 0;
}

function codeForStatus(status: number): string {
  const codes: Record<number, string> = {
    400: 'BAD_REQUEST',
    401: 'AUTH_TOKEN_INVALID',
    403: 'FORBIDDEN',
    404: 'RESOURCE_NOT_FOUND',
    409: 'RESOURCE_CONFLICT',
    422: 'VALIDATION_ERROR',
    429: 'AUTH_RATE_LIMITED',
    502: 'SSO_UNAVAILABLE',
    503: 'DATABASE_UNAVAILABLE',
  };
  return codes[status] ?? 'INTERNAL_ERROR';
}

function responseOf(error: unknown): ErrorResponse | null {
  if (!isRecord(error) || !isRecord(error.response)) return null;
  return error.response as ErrorResponse;
}

function fieldErrorsOf(value: unknown): ApiFieldError[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const fields = value.filter((item): item is Record<string, unknown> => isRecord(item))
    .filter((item) => typeof item.field === 'string' && typeof item.code === 'string' && typeof item.message === 'string')
    .map(item => ({ field: item.field as string, code: item.code as string, message: item.message as string }));
  return fields.length > 0 ? fields : undefined;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId?: string;
  readonly fieldErrors: ApiFieldError[];

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.message);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = status;
    this.requestId = payload.request_id;
    this.fieldErrors = payload.field_errors ?? [];
  }
}

export function toApiError(error: unknown): ApiError | null {
  if (error instanceof ApiError) return error;
  const response = responseOf(error);
  if (!response || !isRecord(response.data)) return null;

  const status = statusCode(response.status);
  const data = response.data;
  const envelope = isRecord(data.error) ? data.error : null;
  const detail = data.detail;

  if (envelope && typeof envelope.message === 'string') {
    return new ApiError({
      code: typeof envelope.code === 'string' ? envelope.code : codeForStatus(status),
      message: envelope.message,
      detail: envelope.detail,
      request_id: typeof envelope.request_id === 'string' ? envelope.request_id : undefined,
      field_errors: fieldErrorsOf(envelope.field_errors),
    }, status);
  }

  // Legacy APIs only exposed `detail`; never stringify arbitrary objects into the UI.
  const message = typeof detail === 'string'
    ? detail
    : isRecord(detail) && typeof detail.message === 'string' ? detail.message : null;
  if (!message) return null;

  return new ApiError({ code: codeForStatus(status), message, detail }, status);
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  return toApiError(error)?.message ?? fallback;
}
